import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Check, Clock, Equal, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  CATEGORIA_LABELS,
  ITEM_STATUS_LABELS,
  type WhatsAppAguardando,
  type WhatsAppConfidence,
  type WhatsAppItemCategoria,
  type WhatsAppItemStatus,
} from "@/types/whatsapp";

// Mesma identidade de cor dos gráficos (paleta validada em WhatsAppMetricas).
const CATEGORIA_VARIANTS: Record<WhatsAppItemCategoria, string> = {
  disponibilidade: "bg-teal-100 text-teal-800",
  negociacao: "bg-blue-100 text-blue-800",
  sentimento: "bg-pink-100 text-pink-800",
};

const STATUS_VARIANTS: Record<WhatsAppItemStatus, string> = {
  pendente: "bg-amber-100 text-amber-800",
  aprovado: "bg-emerald-100 text-emerald-800",
  rejeitado: "bg-muted text-muted-foreground",
  redundante: "bg-muted text-muted-foreground",
};

// Ícone por status: pendente e aprovado ficam no limite da separação para
// daltonismo (ΔE 7.9), então a cor nunca carrega o significado sozinha.
const STATUS_ICONS: Record<WhatsAppItemStatus, LucideIcon> = {
  pendente: Clock,
  aprovado: Check,
  rejeitado: X,
  redundante: Equal,
};

const CONFIDENCE_LABELS: Record<WhatsAppConfidence, string> = {
  alta: "Confiança alta",
  media: "Confiança média",
  baixa: "Confiança baixa",
};

export function WhatsAppCategoriaBadge({ categoria }: { categoria: WhatsAppItemCategoria }) {
  return (
    <Badge variant="secondary" className={cn("font-normal", CATEGORIA_VARIANTS[categoria])}>
      {CATEGORIA_LABELS[categoria]}
    </Badge>
  );
}

export function WhatsAppItemStatusBadge({ status }: { status: WhatsAppItemStatus }) {
  const StatusIcon = STATUS_ICONS[status];
  return (
    <Badge
      variant="secondary"
      className={cn("gap-1 font-normal", STATUS_VARIANTS[status])}
    >
      <StatusIcon className="h-3 w-3" />
      {ITEM_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Confiança é ordinal, então não vira três matizes (verde/âmbar/vermelho
 * falhava a separação até para visão normal). Alta e média ficam como texto
 * discreto; só "baixa" pede atenção, e com ícone além da cor. */
export function WhatsAppConfidenceBadge({ confidence }: { confidence: WhatsAppConfidence }) {
  if (confidence === "baixa") {
    return (
      <Badge variant="secondary" className="gap-1 bg-amber-100 font-normal text-amber-900">
        <AlertTriangle className="h-3 w-3" />
        {CONFIDENCE_LABELS.baixa}
      </Badge>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">
      {CONFIDENCE_LABELS[confidence]}
    </span>
  );
}

// De quem é a próxima mensagem, segundo o resumo da conversa (COR-382).
const AGUARDANDO: Record<WhatsAppAguardando, { label: string; className: string }> = {
  equipe: { label: "Aguardando a equipe", className: "bg-red-100 text-red-800" },
  contato: { label: "Aguardando o contato", className: "bg-amber-100 text-amber-800" },
  ninguem: { label: "Sem pendência", className: "bg-muted text-muted-foreground" },
};

const DIA_EM_MS = 24 * 60 * 60 * 1000;

function haQuantoTempo(desde: string): string | null {
  const dias = Math.floor((Date.now() - new Date(desde).getTime()) / DIA_EM_MS);
  if (dias < 1) return null;
  return dias === 1 ? "há 1 dia" : `há ${dias} dias`;
}

/** `desde` é a última mensagem que o resumo leu: a espera conta a partir dela. */
export function WhatsAppAguardandoBadge({
  aguardando,
  desde,
}: {
  aguardando: WhatsAppAguardando;
  desde?: string;
}) {
  const variante = AGUARDANDO[aguardando];
  const espera = desde && aguardando !== "ninguem" ? haQuantoTempo(desde) : null;
  return (
    <Badge variant="secondary" className={cn("font-normal", variante.className)}>
      {espera ? `${variante.label} ${espera}` : variante.label}
    </Badge>
  );
}
